const productDetails = [
  {
    title: "CNC Tools & Drills",
    description: (
      <>
        We offer a wide range of precision cutting tools essential for CNC
        machining, including{" "}
        <strong>
          Carbide Inserts, End Mill Cutters, Ball Nose Cutters, Rotary Cutters,
          Tap Sets, Reamers, Tool Holder
        </strong>
        , and various types of drills such as{" "}
        <strong>Hammer, Taper Shank</strong>, and{" "}
        <strong>Parallel Shank</strong>. These tools are critical for
        high-performance cutting, drilling, and shaping tasks in industrial
        production lines, especially in metalworking and fabrication industries.
      </>
    ),
    image: "/assets/images/products/cnc.jpg",
    brands: ["/assets/images/brands/cnc_tools/image.png"],
  },
  {
    title: "Bearing Solutions",
    description: (
      <>
        Our bearings support rotational and linear motion in machines while
        minimizing friction and wear. We supply premium{" "}
        <strong>
          Roller Bearings, Ball Bearings, Needle Roller Bearings, Thrust
          Bearings, Tapered Roller Bearings, and Bearing Sleeves
        </strong>
        . These are widely used in heavy machinery, motors, mining equipment,
        and conveyors — enabling smooth operation under high loads and harsh
        conditions.
      </>
    ),
    image: "/assets/images/products/bearings.jpg",
    brands: ["/assets/images/brands/bearing/image.png"],
  },
  {
    title: "Power Transmission Belts",
    description: (
      <>
        Our transmission belt range includes{" "}
        <strong>
          V-Belts, Flat Belts, Timing Belts, Round Belts, Link Belts, and Ribbed
          Belts
        </strong>
        . Designed for efficient power transfer between mechanical components,
        these belts are vital in industrial automation systems, HVAC units, and
        machine tools. We supply both standard and custom belt profiles to suit
        varying tension, speed, and pulley configurations.
      </>
    ),
    image: "/assets/images/products/belts.jpg",
    brands: ["/assets/images/brands/transmission_belts/image.png"],
  },
  {
    title: "Safety Equipment",
    description: (
      <>
        We ensure your workplace stays compliant and protected with essential
        safety gear like{" "}
        <strong>
          Safety Goggles, Industrial Helmets, Leather and Knitted Gloves,
          Earplugs, Face Masks
        </strong>
        , and more. Our safety products are designed to withstand tough
        environments like manufacturing plants, mining sites, and welding bays —
        ensuring both comfort and protection for your workforce.
      </>
    ),
    image: "/assets/images/products/safety.jpg",
  },
  {
    title: "Fasteners",
    description: (
      <>
        From structural bolts to precision screws, we supply all types of
        fasteners in{" "}
        <strong>
          SS 304, SS 202, Mild Steel (4.6), and High Tensile (8.8, 10.9) grades
        </strong>
        . Our fasteners come with optional electroplating for corrosion
        resistance, plastic-film wrapping for moisture protection, and
        station-wise custom packaging — making them ideal for long-haul
        shipments, site-specific use, and industrial installation tasks.
      </>
    ),
    image: "/assets/images/products/fasteners.jpg",
    brands: ["/assets/images/brands/fasteners/image.png"],
  },
  {
    title: "Welding Consumables",
    description: (
      <>
        We provide a complete range of welding consumables including{" "}
        <strong>
          TIG & MIG Torches, Nozzles, Spool Guns, Filler Metals, and Fluxes
        </strong>
        . Our supplies ensure clean welds and durable joints across ferrous and
        non-ferrous applications. Whether you're working in fabrication,
        shipbuilding, construction, or pipelines — we have the right materials
        for your welding station.
      </>
    ),
    image: "/assets/images/products/welding.jpg",
  },
  {
    title: "Packaging Solutions",
    description: (
      <>
        Protect your goods with our range of packaging tools and consumables. We
        offer{" "}
        <strong>
          Tensioners, Sealers, and Strapping Tapes (both plastic and metal)
        </strong>{" "}
        to meet heavy-duty industrial packaging needs. Ideal for bundling metal
        rods, securing crates, or preparing long-distance shipments, our
        solutions ensure reliability and ease of use on factory floors.
      </>
    ),
    image: "/assets/images/products/packaging.jpg",
  },
  {
    title: "Rubber & Felt Products",
    description: (
      <>
        Our rubber and felt solutions serve as essential components in sealing,
        insulation, vibration damping, and filtration. We provide{" "}
        <strong>Rubber Sheets, Neoprene and Viton O-Rings</strong>, as well as
        <strong>Woven and Non-Woven Felt Sheets and Rings</strong>. These
        materials are widely used in machinery assemblies, automotive systems,
        HVAC units, and fluid control systems.
      </>
    ),
    image: "/assets/images/products/rubber.jpg",
  },
];

import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

const Products = () => {
  const location = useLocation();
  const [highlighted, setHighlighted] = useState(null);
  const productRefs = useRef({});

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setHighlighted(id);
      // Scroll into view if not already
      setTimeout(() => {
        const el = productRefs.current[id];
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
      // Remove highlight after 2 seconds
      const timeout = setTimeout(() => setHighlighted(null), 2000);
      return () => clearTimeout(timeout);
    }
  }, [location.hash]);

  return (
    <section className="bg-gray-100 py-16">
      <div className="max-w-7xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-center text-gray-800 mb-12">
          Our Product Range
        </h1>
        <div className="space-y-12 ">
          {productDetails.map((product, index) => {
            const id = product.title.replace(/\s+/g, "-").toLowerCase();
            return (
              <div
                key={index}
                id={id}
                ref={(el) => (productRefs.current[id] = el)}
                className={`bg-white w-4/5 rounded-lg shadow-md p-6 flex flex-col lg:flex-row gap-6 items-center transition-all duration-500 ${
                  highlighted === id ? "ring-4 ring-navy ring-opacity-60" : ""
                }`}
              >
                <img
                  src={product.image}
                  alt={product.title}
                  className="w-full lg:w-60 rounded-md object-cover shadow-sm"
                />
                <div className="text-left">
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">
                    {product.title}
                  </h2>
                  <p className="text-sm text-gray-700">{product.description}</p>
                  {product.brands && product.brands.length > 0 && (
                    <div className="mt-4">
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">
                        Brands We Supply:
                      </h3>
                      <div className="flex gap-4 flex-wrap items-center">
                        {product.brands.map((brandSrc, idx) => (
                          <img
                            key={idx}
                            src={brandSrc}
                            alt="Brand Logo"
                            className="h-32 w-auto object-contain bg-white rounded shadow p-1"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Products;
